import { ILegalClient } from '../Interfaces/ILegalClient';

/** Auflösen der gespeicherten Zahlungsschuldner einer Rechnung zu Objekten.
 *
 *  `Invoice.LegalClients` hält nur IDs. Namen und Erreichbarkeitsangaben
 *  (E-Mail, Portalzugang, Empfang) kommen aus geladenen Mandantenlisten. Ein
 *  Schuldner, der über „Alle Mandanten" gewählt wurde und **nicht** zum Vorgang
 *  gehört, steht in keiner dieser Listen — nach dem Wiederöffnen der Rechnung
 *  war er dadurch unauffindbar. Folge war nicht nur ein Chip ohne Beschriftung:
 *  weil das Auswahlfeld beim Ändern die *sichtbaren* Objekte zurückschreibt,
 *  fiel ein nicht aufgelöster Schuldner beim nächsten Speichern still aus der
 *  Rechnung heraus (QS #8).
 */

/** IDs, die sich mit dem vorhandenen Pool nicht auflösen lassen.
 *  Reihenfolge und Eindeutigkeit bleiben erhalten; unbrauchbare IDs (0,
 *  negativ, null) fallen weg. */
export const missingDebtorIds = (
  selectedIds: number[] | undefined,
  pool: ILegalClient[] | undefined
): number[] => {
  const known = new Set((pool ?? []).map(client => client.idLegalClient));
  const missing: number[] = [];

  for (const id of selectedIds ?? []) {
    if (typeof id !== 'number' || id <= 0) continue;
    if (known.has(id)) continue;
    if (missing.includes(id)) continue;
    missing.push(id);
  }

  return missing;
};

/** Mehrere Mandantenlisten zu einem Pool ohne Duplikate vereinigen.
 *  Bei gleicher ID gewinnt die spätere Quelle — so überschreibt ein frisch
 *  geladener Datensatz einen älteren Stand. */
export const mergeDebtorPool = (
  ...sources: (ILegalClient[] | undefined)[]
): ILegalClient[] =>
  Array.from(
    new Map(
      sources
        .flatMap(source => source ?? [])
        .map(client => [client.idLegalClient, client])
    ).values()
  );

/** Die gespeicherten IDs zu Objekten auflösen, in der Reihenfolge der IDs.
 *  Nicht auflösbare IDs fallen hier heraus — wer sie braucht, fragt
 *  `missingDebtorIds` und lädt sie nach. */
export const resolveDebtors = (
  selectedIds: number[] | undefined,
  pool: ILegalClient[] | undefined
): ILegalClient[] => {
  const byId = new Map(
    (pool ?? []).map(client => [client.idLegalClient, client])
  );

  return (selectedIds ?? [])
    .map(id => byId.get(id))
    .filter((client): client is ILegalClient => client !== undefined);
};
